/**
 * Data relationships module
 * Ensures data consistency and calculates aggregated statistics
 */

import type { Merchant } from '../../shared/types/merchants'
import type { TransactionStatus } from '../../app/types/transactions'
import type { InternalWallet } from './wallets'

/**
 * Extended transaction for internal use with merchant/wallet links
 */
export interface LinkedTransaction {
  id: string
  chargeUuid: string
  merchantId: string
  walletId: string
  amount: number
  status: TransactionStatus
  createdAt: string
}

/**
 * Update merchant statistics from transactions
 */
export function updateMerchantStatistics(
  merchant: Merchant,
  transactions: LinkedTransaction[]
): void {
  const merchantTransactions = transactions.filter(t => t.merchantId === merchant.id)

  if (merchantTransactions.length === 0) {
    return
  }

  const confirmedTransactions = merchantTransactions.filter(t => t.status === 'confirmed')
  const errorTransactions = merchantTransactions.filter(t => t.status === 'error')
  const canceledTransactions = merchantTransactions.filter(t => t.status === 'canceled')

  const totalRevenue = confirmedTransactions.reduce((sum, t) => sum + t.amount, 0)
  const successRate = merchantTransactions.length > 0
    ? confirmedTransactions.length / merchantTransactions.length
    : 0

  // Calculate monthly volume (last 12 months)
  const monthlyVolume = calculateMonthlyVolume(confirmedTransactions)

  // Find last transaction date
  const sortedByDate = [...merchantTransactions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
  const lastTransactionDate = sortedByDate[0]?.createdAt

  merchant.statistics = {
    totalTransactions: merchantTransactions.length,
    totalRevenue: Number(totalRevenue.toFixed(2)),
    averageTicket: confirmedTransactions.length > 0
      ? Number((totalRevenue / confirmedTransactions.length).toFixed(2))
      : 0,
    successRate: Number(successRate.toFixed(4)),
    chargebackRate: Number((canceledTransactions.length / Math.max(1, merchantTransactions.length)).toFixed(4)),
    refundRate: Number((errorTransactions.length * 0.1 / Math.max(1, merchantTransactions.length)).toFixed(4)),
    monthlyVolume,
    lastTransactionDate
  }
}

/**
 * Update wallet statistics from transactions
 */
export function updateWalletStatistics(
  wallet: InternalWallet,
  transactions: LinkedTransaction[]
): void {
  const walletTransactions = transactions.filter(t => t.walletId === wallet.id)

  if (walletTransactions.length === 0) {
    return
  }

  const confirmedTransactions = walletTransactions.filter(t => t.status === 'confirmed')

  // Update basic metrics
  wallet.transaction_count = walletTransactions.length
  wallet.total_volume_usd = Number(
    confirmedTransactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2)
  )
}

/**
 * Calculate monthly volume for last 12 months
 */
function calculateMonthlyVolume(transactions: LinkedTransaction[]): Array<{ month: string, value: number, count?: number }> {
  const monthlyData: Record<string, { value: number, count: number }> = {}

  // Initialize last 12 months
  for (let i = 0; i < 12; i++) {
    const date = new Date()
    date.setMonth(date.getMonth() - i)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    monthlyData[monthKey] = { value: 0, count: 0 }
  }

  // Aggregate transactions
  for (const transaction of transactions) {
    const date = new Date(transaction.createdAt)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    if (monthlyData[monthKey]) {
      monthlyData[monthKey].value += transaction.amount
      monthlyData[monthKey].count += 1
    }
  }

  // Convert to array and sort
  return Object.entries(monthlyData)
    .map(([month, data]) => ({
      month,
      value: Number(data.value.toFixed(2)),
      count: data.count
    }))
    .sort((a, b) => a.month.localeCompare(b.month))
}

/**
 * Validate data relationships
 */
export function validateRelationships(
  merchants: Merchant[],
  wallets: InternalWallet[],
  transactions: LinkedTransaction[]
): { valid: boolean, errors: string[] } {
  const errors: string[] = []

  const merchantIds = new Set(merchants.map(m => m.id))
  const walletIds = new Set(wallets.map(w => w.id))

  // Note: WalletWithMetrics doesn't have merchantId, so we skip that validation

  // Check transaction -> merchant/wallet links
  for (const transaction of transactions) {
    if (!merchantIds.has(transaction.merchantId)) {
      errors.push(`Transaction ${transaction.id} references non-existent merchant ${transaction.merchantId}`)
    }
    if (!walletIds.has(transaction.walletId)) {
      errors.push(`Transaction ${transaction.id} references non-existent wallet ${transaction.walletId}`)
    }
  }

  return {
    valid: errors.length === 0,
    errors
  }
}

/**
 * Build all relationships and update statistics
 */
export function buildRelationships(
  merchants: Merchant[],
  wallets: InternalWallet[],
  transactions: LinkedTransaction[]
): void {
  // Validate first
  const validation = validateRelationships(merchants, wallets, transactions)
  if (!validation.valid) {
    console.warn('Data relationship issues:', validation.errors)
  }

  // Update merchant statistics
  for (const merchant of merchants) {
    updateMerchantStatistics(merchant, transactions)
  }

  // Update wallet statistics
  for (const wallet of wallets) {
    updateWalletStatistics(wallet, transactions)
  }
}
